/**
 * Task Management Constants
 * Status/Priority/ProgressStage IDs for logic only - display data comes from API
 */

// Task Status IDs (for logic/validation only, not display)
export const TASK_STATUS = {
  RE_OPENED: -2,
  ON_HOLD: -1,
  UNSEEN: 0,
  SEEN_IT: 1,
  ON_IT: 2,
  DONE_IT: 3,
  CLOSED: 4,
} as const;

// Task Priority IDs
export const TASK_PRIORITY = {
  LOW: 0,
  MEDIUM: 1,
  HIGH: 2,
} as const;

// Task Progress Stage IDs
export const TASK_PROGRESS_STAGE = {
  CANCELLED: -2,
  ON_HOLD: -1,
  NOT_STARTED: 0,
  INITIATED: 1,
  IN_PROGRESS: 2,
  AWAITING_REVIEW: 3,
  COMPLETED: 4,
} as const;

// Board columns (user board: forward-only 0 -> 1 -> 2 -> 3)
export const USER_BOARD_COLUMNS = [
  TASK_STATUS.UNSEEN,
  TASK_STATUS.SEEN_IT,
  TASK_STATUS.ON_IT,
  TASK_STATUS.DONE_IT,
] as const;

// Project board columns (admin: all statuses except Re Opened)
export const PROJECT_BOARD_COLUMNS = [
  TASK_STATUS.ON_HOLD,
  TASK_STATUS.UNSEEN,
  TASK_STATUS.SEEN_IT,
  TASK_STATUS.ON_IT,
  TASK_STATUS.DONE_IT,
  TASK_STATUS.CLOSED,
] as const;

// Grouping board columns (Campus/Department admin views: active statuses only)
export const GROUPING_BOARD_COLUMNS = [
  TASK_STATUS.ON_HOLD,
  TASK_STATUS.UNSEEN,
  TASK_STATUS.SEEN_IT,
  TASK_STATUS.ON_IT,
  TASK_STATUS.DONE_IT,
] as const;

// Forward-only allowed transitions for user board
export const ALLOWED_FORWARD_TRANSITIONS: Record<number, number[]> = {
  [TASK_STATUS.UNSEEN]: [TASK_STATUS.SEEN_IT, TASK_STATUS.ON_IT, TASK_STATUS.DONE_IT],
  [TASK_STATUS.SEEN_IT]: [TASK_STATUS.ON_IT, TASK_STATUS.DONE_IT],
  [TASK_STATUS.ON_IT]: [TASK_STATUS.DONE_IT],
  [TASK_STATUS.DONE_IT]: [], // No forward from Done
};

/**
 * Checks if a status transition is allowed for non-admin users (forward-only)
 */
export function isForwardTransition(fromStatusId: number, toStatusId: number): boolean {
  return ALLOWED_FORWARD_TRANSITIONS[fromStatusId]?.includes(toStatusId) ?? false;
}

// Statuses considered "active" (user is busy with)
export const ACTIVE_STATUSES = [
  TASK_STATUS.RE_OPENED,
  TASK_STATUS.ON_HOLD,
  TASK_STATUS.SEEN_IT,
  TASK_STATUS.ON_IT,
] as const;

// Statuses considered "completed" (not active)
export const COMPLETED_STATUSES = [
  TASK_STATUS.DONE_IT,
  TASK_STATUS.CLOSED,
] as const;

// Log Type IDs
export const LOG_TYPE = {
  TASK_CREATED: 1,
  COMMENT_ADDED: 2,
  NOTE_ADDED: 3,
  STATUS_CHANGED: 4,
  PRIORITY_CHANGED: 5,
  TASK_VIEWED: 6,
  TASK_ASSIGNED: 7,
  ASSIGNMENT_UPDATED: 8,
  TASK_UPDATED: 9,
  ATTACHMENT_ADDED: 10,
  NOTIFICATION_SENT: 11,
  TASK_DELETED: 12,
  LOOPED_IN_ADDED: 13,
  LOOPED_IN_REMOVED: 14,
  NOTE_ACTIONED: 15,
} as const;

// Notification Type IDs (match dbo.TaskNotificationChannelSetting)
export const NOTIFICATION_TYPE = {
  TASK_LOOPED_IN_ADDED: 32,
  TASK_LOOPED_IN_REMOVED: 33,
  TASK_NOTE_ACTION_REQUESTED: 34,
  TASK_NOTE_ACTIONED: 35,
} as const;

// Page filter keys for usePageFilters
export const FILTER_PAGE_KEYS = {
  ASSIGNED_TASKS: 'tasks/assigned-tasks',
  MY_TASKS: 'tasks/my-tasks',
  ADMIN_ALL_TASKS: 'tasks/admin/all-tasks',
  ADMIN_REPORTS: 'tasks/admin/reports',
  ADMIN_PROJECTS: 'tasks/admin/projects',
  ADMIN_LOCATIONS: 'tasks/admin/locations',
  ADMIN_DEPARTMENTS: 'tasks/admin/departments',
} as const;

// Default minimum working days for task due date
export const MIN_WORKING_DAYS_DUE_DATE = 3;
